import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import classnames from 'classnames';
import UserDatabases from '@Services/UserDatabases';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import get from 'lodash/get';
import CasNumberContainer, { isCasNumberAllowed } from '@Containers/CasNumber';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { ButtonWithIcon } from '@els/els-react--button';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import NumberFormatter from '@Utils/NumberFormatter';

import './SubstanceDetailsContent.scss';

function SubstanceDetailsContent({ structure, metadata, isAllDetailsLoaded, onViewDetails }) {
  const databaseId = get(structure, 'databaseId');
  const molImage = get(structure, 'molImage');
  const currentDatabase = UserDatabases.getDatabaseById(databaseId) || {};
  const details = get(structure, 'details') || {};
  const resultName = get(metadata, 'resultName');

  // Prevent line breaks at hyphens by replacing all hyphens with non-breaking hyphens
  const preventBreaksInBrackets = (text) => {
    if (!text) return text;
    // Simply replace all hyphens with non-breaking hyphens
    return text.replace(/-/g, '&#8209;');
  };

  const titleText = preventBreaksInBrackets(
    (details.chemicalNames && details.chemicalNames[0]) || `${currentDatabase.name} ID: ${details.id}`
  );

  function renderDbSpecificContent() {
    switch (databaseId) {
      case '1':
        return (
          <div className="substance-details pubchem-substance-details">
            <ul className="substance-details-left" aria-label="Substance details">
              <li>
                <span className="rx-btn-text u-els-padding-bottom-1o2">
                  <span>Identification</span>
                </span>
              </li>
              <li>
                {get(structure, 'details.chemicalNameSynonyms.length') && (
                  <span className="rx-btn-text u-els-padding-bottom-1o2">
                    <span>More names</span>
                  </span>
                )}
              </li>
            </ul>
            <div className="substance-details-right pubchem-substance-details-right">
              {map(get(structure, 'pubchemReferences'), (url, title) => (
                <span className="rx-btn-text u-els-padding-bottom-1o2" key={title}>
                  <InnerHTML tag="span" html={title} />
                </span>
              ))}
            </div>
          </div>
        );
      case '5':
      case '15':
      case '16':
        return (
          <div className="substance-details">
            <ul className="substance-details-left">
              <li>
                <span className="rx-btn-text">
                  <span>Identification</span>
                </span>
              </li>
              <li>
                <span className="rx-btn-text">
                  <span>Supplier Information</span>
                </span>
              </li>
            </ul>
            <div className="substance-details-right substance-info">
              <strong className="u-els-font-family-bold">Largest Quantity:</strong>
              <InnerHTML tag="div" html={get(structure, 'supplierData.largestQuantity')} />

              <strong className="u-els-font-family-bold">Average Price/g ($):</strong>
              <InnerHTML tag="div" html={get(structure, 'supplierData.avgPriceGram')} />

              <strong className="u-els-font-family-bold">Average Price/mmol ($):</strong>
              <InnerHTML tag="div" html={get(structure, 'supplierData.avgPriceMMol')} />
            </div>
          </div>
        );
      default: {
        const facts = get(structure, 'facts') || [];
        const links = get(structure, 'links') || {};
        const relatedCounts = [
          { label: 'Preparations', count: links.preparationsCount },
          { label: 'Reactions', count: links.reactionsCount },
          { label: 'Targets', count: links.targetsCount },
          { label: 'Documents', count: links.documentCount },
        ].filter(({ count }) => count > 0);

        return (
          <div className="substance-details substance-info" aria-label="Substance details">
            <ul
              className={classnames('substance-details-left substance-info', facts.length > 4 ? 'substance-details-left--columns' : null)}>
              {facts.map((fact, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index} className="u-els-padding-bottom-1o2">
                  <span>
                    {fact.title}&nbsp;{fact.count && <span>- {fact.count}</span>}
                  </span>
                </li>
              ))}
            </ul>
            <ul className="substance-details-right substance-info" aria-label="Related substance results">
              {relatedCounts.map(({ label, count }) => (
                <li key={label} className="u-els-padding-bottom-1o2">
                  <span>
                    {label}&nbsp;<span>- {NumberFormatter(count)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      }
    }
  }

  return (
    <div>
      <header className="u-els-margin-bottom-1x">
        <InnerHTML
          mode="escapeCustom"
          tag="h2"
          className="substance__title u-els-margin-bottom u-els-display-flex u-els-font-size-h4 substance__title--wrap"
          html={titleText}
        />
        <div className="substance__meta u-els-display-flex u-els-font-size-meta substance-info" aria-label="Substance identifiers">
          {/* formula */}
          {details.formula && (
            <span className="substance__meta__item__content u-els-padding-bottom-1o2">
              <InnerHTML tag="span" html="Linear Structure Formula: " />
              <InnerHTML tag="span" html={details.linearFormula} />
            </span>
          )}
          {/* weight */}
          {details.weight && (
            <span className="substance__meta__item__content u-els-padding-bottom-1o2">
              <InnerHTML tag="span" html="Molecular Weight : " />
              <InnerHTML tag="span" html={details.weight} />
            </span>
          )}
          {/* ID */}
          {details.id && (
            <span className="substance__meta__item__content u-els-padding-bottom-1o2">
              <InnerHTML tag="span" html={`${currentDatabase.name} ID : `} />
              <InnerHTML tag="span" html={details.id} />
            </span>
          )}
          {/* CAS */}
          {!Auth.isFeatureEnabled(FEATURES.disableCasNumberCta) ? (
            <div className="u-els-display-flex u-els-font-size-meta">
              <span className="substance__meta__item__content u-els-padding-bottom-1o2">
                <InnerHTML tag="span" html="CAS Number : " />
              </span>
              <CasNumberContainer
                className="substance__meta__item u-els-padding-bottom-1o2"
                casRegistryNumbers={details.casRegistryNumbers}
                substanceId={details.id}
                databaseId={databaseId}
                name={details.chemicalNames && details.chemicalNames[0]}
                structure={molImage && molImage.structure}
                inChiKey={details.inChiKey}
                withCta={false}
                resultName={resultName}
                showTooltip={false}
              />
            </div>
          ) : (
            isCasNumberAllowed(databaseId) &&
            !isEmpty(details.casRegistryNumbers) && (
              <span className="substance__meta__item__content">
                <InnerHTML tag="span" html="CAS Number : " />
                <InnerHTML
                  tag="span"
                  html={isArray(details.casRegistryNumbers) ? details.casRegistryNumbers?.join(', ') : details.casRegistryNumbers}
                />
              </span>
            )
          )}
        </div>
        {isAllDetailsLoaded && (
          <div className="substance-view-details__footer substance-info-footer">
            <div className="rx-btn-group--left">
              <ButtonWithIcon
                sprite="ChevronRight"
                type="link"
                linkIconSize="xs"
                data-e2e="view-details"
                className="e2e-view-details u-els-margin-bottom-1o8"
                onClick={onViewDetails}>
                View details
              </ButtonWithIcon>
            </div>
          </div>
        )}
      </header>
      {/* Db-Specific Content */}
      {renderDbSpecificContent()}
    </div>
  );
}

SubstanceDetailsContent.propTypes = {
  structure: PropTypes.object.isRequired,
  metadata: MetadataPropTypesShape,
  isAllDetailsLoaded: PropTypes.bool,
  onViewDetails: PropTypes.func,
};

export default SubstanceDetailsContent;
